import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addressForm, setAddressForm] = useState({ label: "Home", full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "", is_default: false });
  const [shipments, setShipments] = useState({});

  const refresh = useCallback(async () => {
    try {
      const [ord, quo, fls, adr] = await Promise.all([
        api.get("/orders"), api.get("/quotes"), api.get("/files/my/list"), api.get("/addresses"),
      ]);
      setOrders(ord.data); setQuotes(quo.data); setFiles(fls.data); setAddresses(adr.data);
      const sh = {};
      await Promise.all(ord.data.filter(o => o.shipment_id).map(async o => {
        try { const { data } = await api.get(`/orders/${o.id}/shipment`); if (data) sh[o.id] = data; }
        catch (err) { logger.warn(`Shipment fetch failed for ${o.id}:`, err); }
      }));
      setShipments(sh);
    } catch (err) {
      logger.error("Dashboard refresh failed:", err);
      toast.error("Unable to load dashboard data");
    }
  }, []);

  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (!user) return;
    refresh();
  }, [user, nav, refresh]);

  const uploadArtwork = async (order_id, e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("order_id", order_id);
    try { await api.post("/uploads/artwork", fd); toast.success("Artwork uploaded"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const approveProof = async (order_id, approved) => {
    const notes = approved ? "" : (prompt("What changes are needed?") || "");
    await api.post(`/orders/${order_id}/proof-decision`, { approved, notes });
    toast.success(approved ? "Proof approved" : "Change request sent");
    refresh();
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    try { await api.post("/addresses", addressForm); toast.success("Address saved"); setAddressForm({ label: "Home", full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "", is_default: false }); refresh(); }
    catch { toast.error("Failed to save"); }
  };

  const removeAddress = async (id) => { await api.delete(`/addresses/${id}`); refresh(); };

  const paidOrders = useMemo(() => orders.filter(o => o.payment_status === "paid"), [orders]);
  const tabs = ["orders", "quotes", "artwork", "invoices", "addresses", "settings"];

  if (user === undefined) return <div className="p-20 text-center font-mono text-xs tracking-widest">LOADING...</div>;

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Dashboard</p>
      <h1 className="font-serif text-5xl md:text-6xl mt-3">Hello, {user?.name?.split(" ")[0]}.</h1>

      <div className="mt-10 border-b border-[#E5E5E5] flex flex-wrap gap-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`dash-tab-${t}`}
            className={`pb-3 text-xs font-mono tracking-[0.25em] uppercase whitespace-nowrap ${tab === t ? "border-b-2 border-[#C5A059] text-[#C5A059]" : "text-[#555] hover:text-[#0a0a0a]"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "orders" && (
          orders.length === 0 ? <p className="text-sm text-[#555]">No orders yet. <Link to="/shop" className="text-[#C5A059]">Browse the shop.</Link></p> :
          <div className="space-y-4">
            {orders.map(o => (
              <div key={o.id} className="border border-[#E5E5E5] p-5" data-testid={`order-${o.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#C5A059]">{o.workflow_stage.replace(/_/g," ")}</p>
                    <p className="font-serif text-xl mt-1">Order #{o.id.slice(0,8).toUpperCase()}</p>
                    <p className="text-xs text-[#888] mt-1">{new Date(o.created_at).toLocaleDateString()} · ${o.amount.toFixed(2)}</p>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-1 border ${o.payment_status === "paid" ? "border-[#1F4D2A] text-[#1F4D2A]" : "border-[#888] text-[#888]"}`}>{o.payment_status}</span>
                </div>
                {o.payment_status === "paid" && o.workflow_stage === "artwork_upload" && (
                  <div className="mt-4 pt-4 border-t border-[#F5F5F5]">
                    <label className="inline-block text-xs font-mono tracking-[0.25em] uppercase bg-[#0a0a0a] text-white px-4 py-2 cursor-pointer hover:bg-[#C5A059] hover:text-[#0a0a0a]" data-testid={`upload-artwork-${o.id}`}>
                      Upload Artwork
                      <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps,.psd,.zip" onChange={(e) => uploadArtwork(o.id, e)} />
                    </label>
                  </div>
                )}
                {o.workflow_stage === "proof_approval" && (
                  <div className="mt-4 pt-4 border-t border-[#F5F5F5]">
                    <p className="text-sm text-[#555] mb-3">A proof is ready for your review.</p>
                    <div className="flex gap-2">
                      <button data-testid={`approve-proof-${o.id}`} onClick={() => approveProof(o.id, true)} className="text-xs font-mono tracking-[0.25em] uppercase bg-[#0a0a0a] text-white px-4 py-2">Approve Proof</button>
                      <button data-testid={`reject-proof-${o.id}`} onClick={() => approveProof(o.id, false)} className="text-xs font-mono tracking-[0.25em] uppercase border border-[#0a0a0a] px-4 py-2">Request Changes</button>
                    </div>
                  </div>
                )}
                {o.approval_history && o.approval_history.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs font-mono tracking-[0.25em] uppercase text-[#555]">History ({o.approval_history.length})</summary>
                    <div className="mt-3 space-y-1 text-xs text-[#555]">
                      {o.approval_history.map((h) => (
                        <p key={`${h.at}-${h.event}`} className="font-mono">· {new Date(h.at).toLocaleString()} — {h.event.replace(/_/g," ")} {h.notes ? `— "${h.notes}"` : ""}</p>
                      ))}
                    </div>
                  </details>
                )}
                {shipments[o.id] && (
                  <div className="mt-3 pt-3 border-t border-[#F5F5F5]" data-testid={`order-shipment-${o.id}`}>
                    <p className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">Shipment · {shipments[o.id].carrier.toUpperCase()} · {shipments[o.id].status.replace(/_/g," ")}</p>
                    <p className="font-mono text-sm">{shipments[o.id].tracking_number}</p>
                    <Link to={`/track/${shipments[o.id].tracking_number}`} className="text-xs text-[#C5A059] border-b border-[#C5A059] mt-1 inline-block">Track shipment →</Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          quotes.length === 0 ? <p className="text-sm text-[#555]">No quotes yet.</p> :
          <div className="space-y-3">
            {quotes.map(q => (
              <div key={q.id} className="border border-[#E5E5E5] p-4" data-testid={`quote-${q.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{q.status}</p>
                    <p className="font-serif text-xl">{q.subject}</p>
                    <p className="text-xs text-[#888]">{q.product_type}{q.dimensions ? ` · ${q.dimensions}` : ""}{q.material ? ` · ${q.material}` : ""}</p>
                    <p className="text-sm text-[#555] mt-2 max-w-2xl">{q.details}</p>
                  </div>
                  {q.price && <p className="font-mono text-2xl text-[#C5A059]">${q.price.toFixed(2)}</p>}
                </div>
                {q.status === "priced" && (
                  <div className="mt-3 pt-3 border-t border-[#F5F5F5]">
                    <button onClick={async () => { await api.patch(`/quotes/${q.id}/approve`); toast.success("Approved"); refresh(); }} data-testid={`approve-quote-${q.id}`} className="text-xs font-mono tracking-[0.25em] uppercase bg-[#0a0a0a] text-white px-4 py-2">Approve Quote</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "artwork" && (
          <div>
            <p className="text-sm text-[#555] mb-6">{files.length} file{files.length === 1 ? "" : "s"} in your library.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {files.map(f => (
                <div key={f.id} className="border border-[#E5E5E5] p-4" data-testid={`file-${f.id}`}>
                  <p className="font-mono text-xs truncate">{f.original_filename}</p>
                  <p className="text-[10px] text-[#888] mt-1 font-mono">v{f.version || 1} · {new Date(f.created_at).toLocaleDateString()}</p>
                  {f.order_id && <p className="text-[10px] text-[#C5A059] font-mono">Order #{f.order_id.slice(0,8)}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "invoices" && (
          <div>
            {paidOrders.length === 0 ? <p className="text-sm text-[#555]">No invoices yet.</p> :
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E5E5] text-left text-xs font-mono uppercase tracking-widest text-[#555]"><th className="py-3">Invoice</th><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>{paidOrders.map(o => (
                <tr key={o.id} className="border-b border-[#F5F5F5]" data-testid={`invoice-${o.id}`}>
                  <td className="py-4 font-mono">INV-{o.id.slice(0,8).toUpperCase()}</td>
                  <td className="text-xs font-mono">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="font-mono text-[#C5A059]">${o.amount.toFixed(2)}</td>
                  <td><span className="text-[10px] font-mono px-2 py-1 border border-[#1F4D2A] text-[#1F4D2A]">PAID</span></td>
                </tr>
              ))}</tbody>
            </table>}
          </div>
        )}

        {tab === "addresses" && (
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-serif text-2xl mb-4">Your addresses</h3>
              {addresses.length === 0 ? <p className="text-sm text-[#555]">No addresses saved.</p> :
              <div className="space-y-3">
                {addresses.map(a => (
                  <div key={a.id} className="border border-[#E5E5E5] p-4" data-testid={`address-${a.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[#C5A059]">{a.label}{a.is_default ? " · default" : ""}</p>
                        <p className="font-serif text-lg mt-1">{a.full_name}</p>
                        <p className="text-sm text-[#555]">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                        <p className="text-sm text-[#555]">{a.city}, {a.state} {a.postal_code}</p>
                        <p className="text-sm text-[#555]">{a.country}</p>
                      </div>
                      <button onClick={() => removeAddress(a.id)} data-testid={`address-delete-${a.id}`} className="text-xs text-[#9A1F1F]">Delete</button>
                    </div>
                  </div>
                ))}
              </div>}
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-4">Add address</h3>
              <form onSubmit={saveAddress} className="space-y-3">
                {[
                  ["label", "Label"], ["full_name", "Full Name", true], ["line1", "Address Line 1", true],
                  ["line2", "Line 2"], ["city", "City", true], ["state", "State"],
                  ["postal_code", "Postal Code", true], ["country", "Country"], ["phone", "Phone"]
                ].map(([k, l, req]) => (
                  <div key={k}>
                    <label className="text-[10px] font-mono tracking-[0.3em] uppercase">{l}</label>
                    <input required={!!req} value={addressForm[k]} onChange={e => setAddressForm({ ...addressForm, [k]: e.target.value })} className="mt-1 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-2 bg-transparent text-sm" data-testid={`address-${k}`} />
                  </div>
                ))}
                <label className="flex items-center gap-2 text-sm text-[#555]">
                  <input type="checkbox" checked={addressForm.is_default} onChange={e => setAddressForm({ ...addressForm, is_default: e.target.checked })} /> Make default
                </label>
                <button data-testid="address-save" className="bg-[#0a0a0a] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold">Save Address</button>
              </form>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-[520px]">
            <h3 className="font-serif text-2xl mb-6">Account settings</h3>
            <div className="space-y-4 border border-[#E5E5E5] p-6">
              <div><p className="text-[10px] font-mono uppercase tracking-widest text-[#888]">Name</p><p className="font-serif text-lg">{user.name}</p></div>
              <div><p className="text-[10px] font-mono uppercase tracking-widest text-[#888]">Email</p><p className="font-mono">{user.email}</p></div>
              <div><p className="text-[10px] font-mono uppercase tracking-widest text-[#888]">Role</p><p className="font-mono">{user.role}</p></div>
              <div><p className="text-[10px] font-mono uppercase tracking-widest text-[#888]">Member Since</p><p className="font-mono text-sm">{new Date(user.created_at).toLocaleDateString()}</p></div>
            </div>
            <Link to="/forgot-password" data-testid="settings-reset" className="mt-6 inline-block text-sm text-[#C5A059] border-b border-[#C5A059]">Reset password</Link>
          </div>
        )}
      </div>
    </section>
  );
}
